import { useCallback } from "react";
import {
    CurrencyType,
    ModelContext,
    ModelVariableScope,
    ModelVariableUnit,
} from "@/models";
import { atom, useAtom, useAtomValue } from "jotai";
import { variableValueFormatter } from "@/util";

const modelContextAtom = atom<ModelContext>(undefined as ModelContext);

export const useModelContext = () => {
    const [modelContext, setModelContext] =
        useAtom<ModelContext>(modelContextAtom);

    return {
        modelContext,
        setModelContext,
    };
};

const currencyAtom = atom<CurrencyType | undefined>((get) => {
    const modelContext = get(modelContextAtom);
    return modelContext?.config?.currency || CurrencyType.USD;
});

const useCurrency = () => {
    const [currency] = useAtom(currencyAtom);
    return currency;
};

const defaultModelVariableScopeAtom = atom<ModelVariableScope | undefined>(
    (get) => {
        const modelContext = get(modelContextAtom);
        return (
            modelContext?.config?.defaultModelVariableScope ||
            ModelVariableScope.Team
        ); // TODO (not required?)
    },
);

export const useDefaultModelVariableScope = () => {
    const defaultModelVariableScope = useAtomValue(
        defaultModelVariableScopeAtom,
    );
    return defaultModelVariableScope;
};

export type VariableValueFormatFunction = (
    unitType: ModelVariableUnit,
    value: number,
    isInteger?: boolean,
    abbrFunc?: (value: number, decimalPlaces: number) => string,
) => string;

export const useFormatModelValue: () => VariableValueFormatFunction = () => {
    const currency = useCurrency();

    const formatVariableValue: VariableValueFormatFunction = useCallback(
        (
            unitType: ModelVariableUnit,
            value: number,
            isInteger?: boolean,
            abbrFunc?: (value: number, decimalPlaces: number) => string,
        ) =>
            variableValueFormatter(
                currency || CurrencyType.USD,
                unitType,
                value,
                isInteger,
                abbrFunc,
            ),
        [currency],
    );

    return formatVariableValue;
};
